// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Resources } from '@shared/localizations/Resources';
import { AccountType } from '@shared/utils/Account/AccountType';
import { MathUtils } from '@shared/utils/MathUtils';
import { AssetInfoData } from './AssetInfoData';
import { AccountAdditionalInfoItem } from '@shared/utils/Account/AdditionalInfoItem';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { InstrumentTradingBalance } from '@shared/utils/Instruments/InstrumentTradingBalance';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { AssetType } from './Asset';
import { AssetBalance } from './AssetBalance';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { AccountSeal, AccountVisibilityType } from './AccountEnums';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { Statistics } from './Statistics';
import { CurrencyUtils } from '@shared/utils/Asset/CurrencyUtils';
import { AccountOperationType } from '../AccountOperations';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
import { StopTradingReason } from '@shared/utils/Account/StopTradingReason';
import { AccountTradeStatusHelper } from '@shared/utils/Account/AccountTradeStatusHelper';
import { CommissionTypes } from '@shared/utils/Commission/CommissionEnums';
import { StringUtils } from '@shared/utils/StringUtils';
import { deserializeServerLimitsConterainers } from './ServerLimits';
import { VolumeLimitsHelper } from '@shared/utils/Account/VolumeLimitsHelper';
// #region AccountDirect
export class Account {
    get CommissionPlansList() {
        const list = [];
        if (this.CommissionPlan) {
            list.push(this.CommissionPlan);
        }
        if (this.CommissionPlan2) {
            list.push(this.CommissionPlan2);
        }
        if (this.CommissionPlan3) {
            list.push(this.CommissionPlan3);
        }
        return list;
    }
    constructor(dataCache) {
        this.User = null;
        this.AccountType = AccountType.SingleCCY;
        this.AccountMode = AccountSeal.Unspecified;
        this.FirstName = '';
        this.LastName = '';
        this.AccountAdditionalInfo = {};
        this.assetBalances = {};
        this.assetInfoData = {};
        this.StockValue = 0;
        this.StockValueByBasis = 0;
        this.StockValueWithoutPnl = 0;
        this.StockValueDiscount = 0;
        this.StartPammCapital = 0;
        this.CurrPammCapital = 0;
        this.MaxDailyLossLimit = 0;
        this.MaxPositions = 0;
        this.MaxPendingOrders = 0;
        this.MaxOrderCapital = 0;
        this.MaxDrawdownLevel = 0;
        this.AccountDescription = '';
        this.TotalMaxPositionsQty = 0;
        this.MaxWeeklyLossLimit = 0;
        this.MaxUrealizedLossLimit = 0;
        this.MaxOrderAmount = 0;
        this.Profit = 0;
        this.LossForUnsettled = 0;
        this.ProfitBase = 0;
        this.ProfitNet = 0;
        this.ProfitForMargin = 0;
        this.ProfitForUnsettled = 0;
        this.OnlyProfitForWithdrawalAvailable = 0;
        this.ProfitNetBase = 0;
        this.OpenOrdAmount = 0;
        this.OpenPosAmount = 0;
        this.OpenPosNumber = 0;
        this.OpenOrdNumber = 0;
        this.BlockedForStocksAccountCurrency = 0;
        this.OptionValue = 0;
        this.PositionsValue = 0;
        this.StopTradingReason = StopTradingReason.NONE;
        this.WaivedMargin = null;
        this.ClusterNode = null;
        this.CommissionPlan = null;
        this.CommissionPlan2 = null;
        this.CommissionPlan3 = null;
        this.CustodialPlanId = null;
        this.FundingRatesPlanId = null;
        this.FreeCommissionAmount = null;
        this.MaxRelativeDrawDownLevel = null;
        this.LossLimitPerTrade = null;
        this.DayTraderPattern = null;
        this.DayTraderPatternProtection = null;
        this.AvailableDayTrades = null;
        this.OnDayTraderPatternChanged = new CustomEvent();
        this.OnUpdateInfoData = new CustomEvent();
        this.OnUpdateAssetBalanceData = new CustomEvent();
        this.Statistics = new Statistics();
        this.InterestPlanID = -1;
        this.FollowerIDs = [];
        this.PreferredInstrumentType = -1;
        this.numberID = null;
        this._volumeLimits = [];
        this.DataCache = dataCache;
    }
    get ID() {
        return this.numberID;
    }
    get Owner() {
        return this.FirstName + ' ' + this.LastName;
    }
    get IsOwnedByUser() {
        return !this.isLinked;
    }
    get BaseCurrency() {
        var _a;
        if (((_a = this.assetBalanceDefault) === null || _a === void 0 ? void 0 : _a.Asset) == null) {
            return this.DataCache.baseCurrency;
        }
        return this.assetBalanceDefault.Asset.Name;
    }
    FillAccount(accountStatusMessage, isUpdate) {
        this.DataCache.CreateOrUpdateUser(accountStatusMessage, this);
        if (accountStatusMessage.UserAdditionalInfo !== null && this.User) {
            this.User.UserAdditionalInfo = accountStatusMessage.UserAdditionalInfo;
        }
        if (accountStatusMessage.AllowOvernightTrading !== null) {
            this.AllowOvernightTrading = accountStatusMessage.AllowOvernightTrading;
        }
        if (accountStatusMessage.CommisionId !== null) {
            this.CommisionId = accountStatusMessage.CommisionId;
            if (this.DataCache !== null) {
                this.CommissionPlan = this.DataCache.TryGetCommissionPlan(accountStatusMessage.CommisionId);
            }
        }
        if (accountStatusMessage.CommisionId2 !== null) {
            this.CommisionId2 = accountStatusMessage.CommisionId2;
            if (this.DataCache !== null) {
                this.CommissionPlan2 = this.DataCache.TryGetCommissionPlan(accountStatusMessage.CommisionId2);
            }
        }
        if (accountStatusMessage.CommisionId3 !== null) {
            this.CommisionId3 = accountStatusMessage.CommisionId3;
            if (this.DataCache !== null) {
                this.CommissionPlan3 = this.DataCache.TryGetCommissionPlan(accountStatusMessage.CommisionId3);
            }
        }
        if (accountStatusMessage.SpreadPlanId !== null) {
            this.SpreadPlanId = accountStatusMessage.SpreadPlanId;
            if (this.DataCache !== null) {
                this.SpreadPlan = this.DataCache.TryGetSpreadPlan(accountStatusMessage.SpreadPlanId);
            }
        }
        if (accountStatusMessage.CrossratesPlanId !== null) {
            this.CrossratesPlanId = accountStatusMessage.CrossratesPlanId;
            if (this.DataCache !== null) {
                this.CrossratesPlan = this.DataCache.TryGetCrossratesPlan(accountStatusMessage.CrossratesPlanId);
            }
        }
        if (accountStatusMessage.RiskPlanId !== null) {
            this.RiskPlanId = accountStatusMessage.RiskPlanId;
            if (this.DataCache !== null) {
                this.RiskPlan = this.DataCache.TryGetRiskPlan(accountStatusMessage.RiskPlanId);
            }
        }
        if (accountStatusMessage.SwapPlanId !== null) {
            this.SwapPlanId = accountStatusMessage.SwapPlanId;
            if (this.DataCache !== null) {
                this.SwapPlan = this.DataCache.TryGetSwapPlan(accountStatusMessage.SwapPlanId);
            }
        }
        if (accountStatusMessage.ChargingPlanId !== null) {
            this.ChargingPlanId = accountStatusMessage.ChargingPlanId;
            if (this.DataCache !== null) {
                this.ChargingPlan = this.DataCache.TryGetChargingPlan(accountStatusMessage.ChargingPlanId);
            }
        }
        if (accountStatusMessage.MarginLevel !== null) {
            this.MarginLevel = accountStatusMessage.MarginLevel;
        }
        if (accountStatusMessage.WarningLevel !== null) {
            this.WarningLevel = accountStatusMessage.WarningLevel;
        }
        if (accountStatusMessage.TradingLevel !== null) {
            this.TradingLevel = accountStatusMessage.TradingLevel;
        }
        if (accountStatusMessage.BstrAccount !== null) {
            this.BstrAccount = accountStatusMessage.BstrAccount;
            this.AcctNumber = accountStatusMessage.BstrAccount;
            this.numberID = accountStatusMessage.AccountId;
        }
        if (accountStatusMessage.IsMasterAccount !== null) {
            this.isMasterAccount = accountStatusMessage.IsMasterAccount;
        }
        if (accountStatusMessage.FollowerIDs !== null) {
            this.FollowerIDs = accountStatusMessage.FollowerIDs;
            if (isUpdate) {
                Account.FollowerIDsChanged.Raise();
            }
        }
        if (accountStatusMessage.UserID !== null) {
            this.userID = accountStatusMessage.UserID;
        }
        if (accountStatusMessage.UserLogin !== null) {
            this.userLogin = accountStatusMessage.UserLogin;
        }
        if (accountStatusMessage.FundType !== null) {
            this.FundType = accountStatusMessage.FundType;
        }
        if (accountStatusMessage.TodayTrades !== null) {
            this.TodayTrades = accountStatusMessage.TodayTrades;
        }
        if (accountStatusMessage.Email !== null) {
            this.Email = accountStatusMessage.Email;
        }
        if (accountStatusMessage.TelephoneNumber != null) {
            this.TelephoneNumber = accountStatusMessage.TelephoneNumber;
        }
        if (accountStatusMessage.FirstName !== null) {
            this.FirstName = accountStatusMessage.FirstName;
        }
        if (accountStatusMessage.MiddleName !== null) {
            this.MiddleName = accountStatusMessage.MiddleName;
        }
        if (accountStatusMessage.LastName !== null) {
            this.LastName = accountStatusMessage.LastName;
        }
        if (accountStatusMessage.AccountDescription !== null) {
            this.AccountDescription = accountStatusMessage.AccountDescription;
        }
        if (accountStatusMessage.AccountType !== null) {
            this.AccountType = accountStatusMessage.AccountType;
        }
        if (accountStatusMessage.IntradayRiskManagement !== null) {
            this.IntradayRiskManagement = accountStatusMessage.IntradayRiskManagement;
        }
        if (accountStatusMessage.AccountTradeStatus !== null) {
            this.AccountTradeStatus = accountStatusMessage.AccountTradeStatus;
            if (isUpdate) {
                Account.TradeStatusChanged.Raise();
            }
        }
        if (accountStatusMessage.AllowTradingOnPrepostMarket !== null) {
            this.AllowTradingOnPrepostMarket = accountStatusMessage.AllowTradingOnPrepostMarket;
        }
        if (accountStatusMessage.MaxDailyLossLimit !== null) {
            this.MaxDailyLossLimit = accountStatusMessage.MaxDailyLossLimit;
        }
        if (accountStatusMessage.MaxWeeklyLossLimit !== null) {
            this.MaxWeeklyLossLimit = accountStatusMessage.MaxWeeklyLossLimit;
        }
        if (accountStatusMessage.TotalMaxPositionsQty !== null) {
            this.TotalMaxPositionsQty = accountStatusMessage.TotalMaxPositionsQty;
        }
        // if (accountStatusMessage.MaxOrdersPerDay !== null)
        //     this.MaxOrdersPerDay = accountStatusMessage.MaxOrdersPerDay;
        if (accountStatusMessage.Pin !== null) {
            this.userPin = accountStatusMessage.Pin;
        }
        if (accountStatusMessage.Login !== null && !this.FullAccString) {
            this.FullAccString = accountStatusMessage.Login;
        }
        if (accountStatusMessage.AccountMode !== null) {
            this.AccountMode = accountStatusMessage.AccountMode;
        }
        if (accountStatusMessage.PreferredInstrumentType !== null) {
            this.PreferredInstrumentType = accountStatusMessage.PreferredInstrumentType;
        }
        if (accountStatusMessage.MaxPositions !== null) {
            this.MaxPositions = accountStatusMessage.MaxPositions;
        }
        if (accountStatusMessage.MaxPendingOrders !== null) {
            this.MaxPendingOrders = accountStatusMessage.MaxPendingOrders;
        }
        if (accountStatusMessage.MaxPendingOrdersValue !== null) {
            this.MaxPendingOrdersValue = accountStatusMessage.MaxPendingOrdersValue;
        }
        if (accountStatusMessage.MaxOrderCapital !== null) {
            this.MaxOrderCapital = accountStatusMessage.MaxOrderCapital;
        }
        if (accountStatusMessage.MaxDrawdownLevel !== null) {
            this.MaxDrawdownLevel = accountStatusMessage.MaxDrawdownLevel;
        }
        if (accountStatusMessage.StopTradingReason !== null) {
            this.StopTradingReason = accountStatusMessage.StopTradingReason;
        }
        if (accountStatusMessage.InterestsOnBalancePlanID !== null) {
            this.InterestPlanID = accountStatusMessage.InterestsOnBalancePlanID;
        }
        if (accountStatusMessage.IsSigned !== null) {
            this /* .user */.IsSigned = accountStatusMessage.IsSigned;
        }
        // allowedRoutes.Clear();
        if (accountStatusMessage.AllowedRoutes) {
            this.allowedRoutes = accountStatusMessage.AllowedRoutes.split(';');
        }
        if (accountStatusMessage.AccountVisibilityFlag != null) {
            // не перезатирать! мессадж с апдейтами не содержат это поле
            this.AccountVisibilityFlag = accountStatusMessage.AccountVisibilityFlag;
            this.isLinked = this.AccountVisibilityFlag === AccountVisibilityType.VISIBILITY_FLAG_LINK;
        }
        if (accountStatusMessage.AssetSharesNames !== null) {
            this.AssetSharesNames = accountStatusMessage.AssetSharesNames;
        }
        if (accountStatusMessage.MaxUrealizedLossLimit !== null) {
            this.MaxUrealizedLossLimit = accountStatusMessage.MaxUrealizedLossLimit;
        }
        if (accountStatusMessage.MaxRelativeDrawDownLevel !== null) {
            this.MaxRelativeDrawDownLevel = accountStatusMessage.MaxRelativeDrawDownLevel;
        }
        if (accountStatusMessage.LossLimitPerTrade !== null) {
            this.LossLimitPerTrade = accountStatusMessage.LossLimitPerTrade;
        }
        if (accountStatusMessage.MaxOrderAmount !== null) {
            this.MaxOrderAmount = accountStatusMessage.MaxOrderAmount;
        }
        if (accountStatusMessage.WaivedMargin !== null) {
            this.WaivedMargin = accountStatusMessage.WaivedMargin;
        }
        // if (accountStatusMessage.InitialMarginWithoutWaived !== null)
        //     this.InitialMarginWithoutWaived = accountStatusMessage.InitialMarginWithoutWaived;
        this.ExtendedFields = accountStatusMessage.ExtendedFields;
        /// //
        if (accountStatusMessage.ClusterNode !== null) {
            this.ClusterNode = accountStatusMessage.ClusterNode;
        }
        if (accountStatusMessage.CustodialPlanId !== null) {
            this.CustodialPlanId = accountStatusMessage.CustodialPlanId;
        }
        if (accountStatusMessage.FundingRateMarkupPlanId !== null) {
            this.FundingRatesPlanId = accountStatusMessage.FundingRateMarkupPlanId;
        }
        if (accountStatusMessage.DayTraderPattern !== null) {
            this.DayTraderPattern = accountStatusMessage.DayTraderPattern;
            if (isUpdate) {
                this.OnDayTraderPatternChanged.Raise();
            }
        }
        if (accountStatusMessage.DayTraderPatternProtection !== null) {
            this.DayTraderPatternProtection = accountStatusMessage.DayTraderPatternProtection;
        }
        if (accountStatusMessage.AvailableDayTrades !== null) {
            this.AvailableDayTrades = accountStatusMessage.AvailableDayTrades;
        }
        if (accountStatusMessage.StatisticsGroup !== null) {
            this.Statistics.FillByMessage(accountStatusMessage.StatisticsGroup);
        }
        if (isValidNumber(accountStatusMessage.FreeCommissionAmount)) {
            this.FreeCommissionAmount =
                accountStatusMessage.FreeCommissionAmount !== -1 ? accountStatusMessage.FreeCommissionAmount : null;
        }
        this.tryUpdateAssetBalances(accountStatusMessage.AssetBalances);
        this.tryUpdateAssetInfoData(accountStatusMessage.AssetInfoData);
        this.tryUpdateAccountAdditionalInfo(accountStatusMessage.AccountAdditionalInfo);
    }
    tryUpdateAccountAdditionalInfo(accountAdditionalInfo) {
        if (!accountAdditionalInfo) {
            return;
        }
        for (const key in accountAdditionalInfo) {
            const item = accountAdditionalInfo[key];
            if (this.AccountAdditionalInfo[item.APIKey]) {
                this.AccountAdditionalInfo[item.APIKey].Update(item);
            }
            else {
                const newItem = new AccountAdditionalInfoItem();
                newItem.Update(item);
                this.AccountAdditionalInfo[item.APIKey] = newItem;
            }
        }
    }
    tryUpdateAssetBalances(assetBalanceArray) {
        if (!assetBalanceArray) {
            return;
        }
        // TODO.
        const dc = this.DataCache;
        const assetBalances = this.assetBalances;
        let AssetAdditionalInfoUpdated = false;
        for (let i = 0, len = assetBalanceArray.length; i < len; i++) {
            const balanceMessage = assetBalanceArray[i];
            const asset = dc.GetAssetById(balanceMessage.AssetId);
            if (!asset)
                continue;
            let balance = assetBalances[asset.Name];
            if (!balance) {
                balance = assetBalances[asset.Name] = new AssetBalance(this, balanceMessage.IsEmpty);
            }
            else if (balance.IsEmpty && !balanceMessage.IsEmpty) {
                balance.RecreateData();
            }
            // Fill AssetBalance
            if (!balance.IsEmpty) {
                AssetAdditionalInfoUpdated = balance.FillByMessage(balanceMessage) || AssetAdditionalInfoUpdated;
            }
            if (!balance.Asset && dc) {
                balance.Asset = asset;
            }
            if (!this.assetBalanceDefault || (balance.Asset && dc && balance.Asset.Name === dc.baseCurrency)) {
                this.assetBalanceDefault = balance;
            }
        }
        if (AssetAdditionalInfoUpdated) {
            this.OnUpdateAssetBalanceData.Raise();
        }
    }
    tryUpdateAssetInfoData(assetInfoDataArray) {
        if (!assetInfoDataArray) {
            return;
        }
        const assetInfoData = this.assetInfoData;
        // TODO.
        const dc = this.DataCache;
        for (let i = 0, len = assetInfoDataArray.length; i < len; i++) {
            const infoDataMessage = assetInfoDataArray[i];
            const asset = dc.GetAssetByName(infoDataMessage.Name);
            if (!asset)
                continue;
            let infoData = assetInfoData[asset.Name];
            if (!infoData) {
                infoData = assetInfoData[asset.Name] = new AssetInfoData();
            }
            infoData.CalculationMethod = infoDataMessage.CalculationMethod;
            infoData.Name = infoDataMessage.Name;
            infoData.TodayTradedQty = infoDataMessage.TodayTradedQty;
            infoData.TotalQuantity = infoDataMessage.TotalQuantity;
            infoData.TotalQuantityForMarginAvailable = infoDataMessage.TotalQuantityForMarginAvailable;
            infoData.AssetTradingMode = infoDataMessage.AssetTradingMode;
            this.OnUpdateInfoData.Raise(asset);
        }
    }
    toString(withAsset = false) {
        var _a, _b, _c;
        const result = this.FullAccString;
        const dataCache = this.DataCache;
        const accNum = !isNullOrUndefined(dataCache) ? dataCache.getNumberOfAccounts() : 0;
        if (!withAsset || accNum <= 1)
            return result;
        const singleCurrency = dataCache.userHasSameCurrencyOnAllAccounts(); /* #116404 */
        const isMultAsset = this.AccountType === AccountType.MultiAsset; /*     #116419 */
        if (singleCurrency || isMultAsset)
            return result; /*                    #103422 */
        const assetName = (_c = (_b = (_a = this.assetBalanceDefault) === null || _a === void 0 ? void 0 : _a.Asset) === null || _b === void 0 ? void 0 : _b.Name) !== null && _c !== void 0 ? _c : '';
        return isValidString(assetName) ? `${result} [${assetName}]` : result;
    }
    get TrailingDrawdownLevel() {
        return this.assetBalanceDefault !== null ? this.assetBalanceDefault.TrailingDrawdownLevel : -1;
    }
    getCrossPrice() {
        // Раньше сервер присылал обратный кросс - для перевода из валюты сервера в валюту аккаунта
        const acc = this.DataCache.GetAccountByIdOrName(this.FullAccString);
        return this.DataCache.CrossRateCache.GetCrossPriceExp1(this.DataCache.baseCurrency, acc);
    }
    // MB CORRCECT
    getCrossPriceCorrect() {
        // Раньше сервер присылал обратный кросс - для перевода из валюты сервера в валюту аккаунта
        const acc = this.DataCache.GetAccountByIdOrName(this.FullAccString);
        return 1 / this.DataCache.CrossRateCache.GetCrossPriceExp1(this.BaseCurrency, acc);
    }
    formatPrice(value, noCurrency = false) {
        if (!isNullOrUndefined(this.assetBalanceDefault)) {
            return this.assetBalanceDefault.formatPrice(value, noCurrency);
        }
        return this.formatString(value, noCurrency);
    }
    roundFormatPrice(value, noCurrency = false) {
        if (!isNullOrUndefined(this.assetBalanceDefault)) {
            return this.assetBalanceDefault.roundFormatPrice(value, noCurrency);
        }
        return this.formatString(value, noCurrency);
    }
    formatString(value, noCurrency = false) {
        if (noCurrency) {
            return PriceFormatter.formatPrice(value, Account.point);
        }
        else {
            return PriceFormatter.formatPrice(value, Account.point) + ' ' + this.DataCache.baseCurrency;
        }
    }
    // #120545
    roundPrice(price) {
        if (isNaN(price)) {
            return 0;
        }
        let pointSize = Account.point;
        if (!isNullOrUndefined(this.assetBalanceDefault)) {
            pointSize = this.assetBalanceDefault.Asset.Point;
        }
        return MathUtils.TruncateDouble(price, pointSize);
    }
    get StockValueCrossPrice() {
        return this.StockValue / this.getCrossPrice();
    }
    get StockValueWithoutPnlCrossPrice() {
        return this.StockValueWithoutPnl / this.getCrossPrice();
    }
    get StockValueDiscountCrossPrice() {
        return this.StockValueDiscount / this.getCrossPrice();
    }
    get StartPammCapitalCrossPrice() {
        return this.StartPammCapital / this.getCrossPrice();
    }
    get CurrPammCapitalCrossPrice() {
        return this.CurrPammCapital / this.getCrossPrice();
    }
    get CurrentDailyProfit() {
        return this.assetBalanceDefault !== null ? this.assetBalanceDefault.CurrentDailyProfit : -1;
    }
    get CurrentDailyLoss() {
        return this.assetBalanceDefault !== null ? this.assetBalanceDefault.CurrentDailyLoss : -1;
    }
    get CurrentWeeklyLoss() {
        return this.assetBalanceDefault !== null ? this.assetBalanceDefault.CurrentWeeklyLoss : -1;
    }
    get BalancePlusAllRisks() {
        return (this.assetBalanceDefault.assetBalanceData.balance +
            this.StockValueDiscount +
            this.ProfitForUnsettled +
            this.LossForUnsettled +
            this.assetBalanceDefault.UnsettledLoss +
            this.assetBalanceDefault.UnsettledProfit +
            this.UnsettledCashForStocks +
            this.UnsettledOptionPremium -
            this.assetBalanceDefault.UnusedPremiumFromOpenSell +
            this.assetBalanceDefault.UnsettledCollateral /* 82717 */ -
            this.assetBalanceDefault.UnusedSettledCashForStocks /* 98161 */ -
            this.assetBalanceDefault.UnusedSettledCollateral /* 98161 */ +
            this.assetBalanceDefault.UnsettledDeposit +
            this.UnsettledCashForFixedIncome +
            this.assetBalanceDefault.UsedUnsettledNegativeAccruedInterest +
            this.assetBalanceDefault.UnsettledPositiveAccruedInterest);
    }
    get BalancePlusAllRisksCrossPrice() {
        return this.BalancePlusAllRisks / this.getCrossPrice();
    }
    get VolumeLimits() {
        const serializedVolumeLimit = this.DataCache.getRuleStringValueForAccount(RulesSet.VALUE_INSTRUMENT_TYPE_LIMIT, this, '');
        if (serializedVolumeLimit !== this._serializedVolumeLimits) {
            this._serializedVolumeLimits = serializedVolumeLimit;
            if (isValidString(this._serializedVolumeLimits)) {
                this._volumeLimits = deserializeServerLimitsConterainers(this._serializedVolumeLimits);
            }
            else {
                this._volumeLimits = [];
            }
        }
        return this._volumeLimits;
    }
    get UnsettledCashForStocks() {
        if (this.assetBalanceDefault != null) {
            return (this.assetBalanceDefault.UsedUnsettledNegativeCashForStocks +
                this.assetBalanceDefault.UnsettledPositiveCashForStocks);
        }
        else {
            return 0;
        }
    }
    get UnsettledCashForFixedIncome() {
        if (this.assetBalanceDefault != null) {
            return (this.assetBalanceDefault.UsedUnsettledNegativeCashForFixedIncome +
                this.assetBalanceDefault.UnsettledPositiveCashForFixedIncome);
        }
        else {
            return 0;
        }
    }
    get UnsettledOptionPremium() {
        return (this.assetBalanceDefault.UsedUnsettledNegativePremium +
            this.assetBalanceDefault.UnsettledPositivePremium -
            this.assetBalanceDefault.UnsettledPremiumFromOpenSell);
    }
    get StockOrdersReq() {
        return this.assetBalanceDefault != null ? this.assetBalanceDefault.StockOrdersReq : 0;
    }
    get StockOrdersReqCrossPrice() {
        return this.StockOrdersReq / this.getCrossPrice();
    }
    get WithdrawalAvailable() {
        return this.assetBalanceDefault != null ? this.assetBalanceDefault.WithdrawalAvaliable(this) : 1;
    }
    get assetName() {
        return this.assetBalanceDefault.Asset.Name;
    }
    get BlockedForStocks() {
        return this.assetBalanceDefault != null ? this.assetBalanceDefault.assetBalanceData.blockedForStocks : 0;
    }
    isAllowOTHTrading(instrument) {
        if (isNullOrUndefined(instrument) || isNullOrUndefined(this.DataCache)) {
            return false;
        }
        return (this.DataCache.RulesCache.IsAllowedForAccount(RulesSet.FUNCTION_ALLOW_OTH_TRADING, this) &&
            instrument.isAllowOTHTrading);
    }
    /// <summary>
    /// Поиск ассетбаланса, правильный его пользовать везде
    /// </summary>
    GetAssetBalanceCorrect(instrument, isBuy) {
        if (this.AccountType !== AccountType.MultiAsset) {
            return this.assetBalanceDefault;
        }
        const assetBalances = this.assetBalances;
        if (!assetBalances) {
            return null;
        }
        else if (!isNullOrUndefined(instrument)) {
            let res = null;
            if (instrument.TradingBalance === InstrumentTradingBalance.SETTLEMENT_IMMEDIATE) {
                if (isBuy) {
                    res = assetBalances[instrument.Exp2];
                }
                else {
                    res = assetBalances[instrument.Exp1];
                }
            }
            else {
                res = assetBalances[instrument.Exp2];
            }
            return res || null;
        }
        else if (isNullOrUndefined(instrument)) {
            return this.assetBalanceDefault;
        }
        return null;
    }
    /// <summary>
    /// Поиск ассетбаланса
    /// </summary>
    GetAssetBalanceByName(assetName) {
        return this.assetBalances[assetName] || null;
    }
    GetAssetDataInfoByName(assetName) {
        return this.assetInfoData[assetName] || null;
    }
    GetCommissionInfo(instrument) {
        const commissionItem = this.CommissionPlan ? this.CommissionPlan.GetCommissionItem(instrument) : null;
        const additionalInfo = commissionItem ? commissionItem.GetCommissionInfo() : '';
        const key = 'screen.closePosition.additionalInfoLabel';
        const label = Resources.getResource(key) + ':';
        return additionalInfo && !Resources.isHidden(key) ? '<br>' + label + '<br>' + additionalInfo : '';
    }
    GetAllowedCommissionTypes(instrument) {
        var _a;
        const result = {
            [CommissionTypes.SwapBuy]: true,
            [CommissionTypes.SwapSell]: true
        };
        for (const plan of (_a = this.CommissionPlansList) !== null && _a !== void 0 ? _a : []) {
            const planTypes = plan.GetAllowedCommissionTypes(instrument);
            for (const key in planTypes) {
                if (planTypes[key]) {
                    result[key] = true;
                }
            }
        }
        return result;
    }
    formatPriceShortCurrency(price) {
        var _a;
        const asset = (_a = this.assetBalanceDefault) === null || _a === void 0 ? void 0 : _a.Asset;
        if (asset) {
            const formattedPrice = asset.formatPrice(price, true);
            return CurrencyUtils.FormatWithCurrencySymbol(formattedPrice, asset.Name);
        }
        else {
            return this.formatPrice(price, false);
        }
    }
    getInterestByAsset(asset) {
        return this.getInterestValueInternal(asset, false);
    }
    getNegativeInterestByAsset(asset) {
        return this.getInterestValueInternal(asset, true);
    }
    getInterestValueInternal(asset, isNegative) {
        var _a, _b;
        if (!asset || this.InterestPlanID === -1)
            return 0;
        const plan = this.InterestPlan;
        return isNegative ? ((_a = plan === null || plan === void 0 ? void 0 : plan.getNegativeInterestValue(asset.Id)) !== null && _a !== void 0 ? _a : 0) : ((_b = plan === null || plan === void 0 ? void 0 : plan.getInterestValue(asset.Id)) !== null && _b !== void 0 ? _b : 0);
    }
    get InterestPlan() {
        return this.DataCache.TryGetInterestPlan(this.InterestPlanID);
    }
    equals(account) {
        return this === account;
    }
    static IsEqualAccount(a1, a2) {
        if (!a1 || !a2) {
            return false;
        }
        return a1.AcctNumber === a2.AcctNumber;
    }
    // #endregion
    static GetInitialMarginSize(account) {
        const rp = account.RiskPlan.riskPlanCache.getDefaultRiskPlanSettings();
        if (!rp) {
            return null;
        }
        const productTypes = rp.availableProductTypes;
        const prodType = productTypes.length > 1 ? ProductType.Intraday : productTypes[0];
        const rpCashItem = rp.cache[prodType];
        if (!rpCashItem) {
            return null;
        }
        return rpCashItem.GetInitMargin();
    }
    static GetAccountFeature(key, account, assetBalance, estimatedCurrency = undefined) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (estimatedCurrency === undefined)
            estimatedCurrency = null;
        if (account == null || assetBalance == null) {
            return 0;
        }
        const UseAccCurrency = true; // BaseApplication.App.MultiDataCache.UseAccCurrency;
        let initialMarginSize = null;
        switch (key) {
            // General
            case AccountFeature.Balance:
                return assetBalance.assetBalanceData.balance;
            case AccountFeature.ProjectedBalance:
                return assetBalance.assetBalanceData.projectedBalance;
            case AccountFeature.BalancePlusAllRisks:
                return account.AccountType === AccountType.MultiAsset
                    ? assetBalance.assetBalanceData.balance
                    : account.BalancePlusAllRisks;
            case AccountFeature.CreditValue:
                return assetBalance.assetBalanceData.creditValue;
            case AccountFeature.AvailableFunds:
                return assetBalance.assetBalanceData.availableFunds;
            case AccountFeature.BlockedBalance:
                return assetBalance.assetBalanceData.blockedSum;
            case AccountFeature.CashBalance:
                return assetBalance.assetBalanceData.cashBalance;
            case AccountFeature.WithdrawalAvailable:
                return account.AccountType === AccountType.MultiAsset
                    ? assetBalance.WithdrawalAvaliable(account)
                    : account.WithdrawalAvailable;
            case AccountFeature.InterestRatePercent:
                return account.getInterestByAsset(assetBalance.Asset) * 100;
            case AccountFeature.NegativeInterestRatePercent:
                return account.getNegativeInterestByAsset(assetBalance.Asset) * 100;
            case AccountFeature.EstimateValue: {
                let ec = 0;
                if (estimatedCurrency != null) {
                    if (Object.keys(account.assetBalances).length > 1) {
                        for (const abKey in account.assetBalances) {
                            const ab = account.assetBalances[abKey];
                            if (ab.Asset.Type === AssetType.CURRENCY || ab.Asset.Type === AssetType.CRYPTO_CCY) {
                                ec +=
                                    ab.assetBalanceData.balance *
                                        account.DataCache.CrossRateCache.GetCrossPriceExp1Exp2(ab.Asset.Name, estimatedCurrency.Name, account);
                            }
                        }
                    }
                    else {
                        ec =
                            assetBalance.assetBalanceData.balance *
                                account.DataCache.CrossRateCache.GetCrossPriceExp1Exp2(assetBalance.Asset.Name, estimatedCurrency.Name, account);
                    }
                }
                else {
                    ec = assetBalance.assetBalanceData.balance;
                }
                return ec + account.BlockedForStocks;
            }
            // Margin
            case AccountFeature.MarginUsed:
                return (assetBalance.assetBalanceData.minMargin +
                    assetBalance.assetBalanceData.blockedForOrders +
                    assetBalance.assetBalanceData.marginDeficiency);
            case AccountFeature.MarginUsedPercent:
                return assetBalance.InitialMarginReqPercent;
            case AccountFeature.RiskLevelPercent:
                return assetBalance.MaintenanceMarginReqPercent;
            case AccountFeature.WarnMarginReq:
                return assetBalance.WarningMarginRequirement;
            case AccountFeature.WarnMarginReqPercent:
                return assetBalance.WarningMarginReqPercent;
            case AccountFeature.MarginBeforeWarning:
                return assetBalance.MarginBeforeWarning;
            case AccountFeature.InitialMarginWithoutWaived:
                return assetBalance.assetBalanceData.InitialMarginWithoutWaived;
            case AccountFeature.MarginAvailable:
                return assetBalance.assetBalanceData.availableMargin;
            case AccountFeature.MarginWarningLevel:
                return account.WarningLevel;
            case AccountFeature.WarningMargin: {
                let risks = account.AccountType === AccountType.MultiAsset
                    ? assetBalance.assetBalanceData.balance - assetBalance.StockOrdersReq
                    : account.BalancePlusAllRisks -
                        account.StockOrdersReq +
                        assetBalance.assetBalanceData.StocksLiquidity;
                risks -= assetBalance.assetBalanceData.OptionPremiumReq + assetBalance.FixedIncomeOrdersReq;
                return risks * (account.WarningLevel / 100);
            }
            case AccountFeature.TotalMaintReq:
                return assetBalance.assetBalanceData.maintanceMargin;
            case AccountFeature.BlockedForStocks:
                return assetBalance.assetBalanceData.blockedForStocks;
            case AccountFeature.UnsettledCash:
                return assetBalance.assetBalanceData.reservedBalance;
            case AccountFeature.StockValue:
                return UseAccCurrency ? account.StockValue : account.StockValueCrossPrice;
            case AccountFeature.StocksOrdersReq:
                return assetBalance.StockOrdersReq;
            case AccountFeature.OptionValue:
                return account.OptionValue;
            case AccountFeature.StocksLiquidity:
                return assetBalance.assetBalanceData.StocksLiquidity;
            case AccountFeature.CurrentTradingStatus:
                return account.AccountTradeStatus;
            case AccountFeature.VolumeLimitForAll:
                return this._volumeLimitsHelper.getLimitValues(account, assetBalance, InstrumentTypes.ALL).limitValue;
            case AccountFeature.VolumeLimitForEquities:
                return this._volumeLimitsHelper.getLimitValues(account, assetBalance, InstrumentTypes.EQUITIES)
                    .limitValue;
            case AccountFeature.VolumeLimitForFutures:
                return this._volumeLimitsHelper.getLimitValues(account, assetBalance, InstrumentTypes.FUTURES)
                    .limitValue;
            case AccountFeature.VolumeLimitForOptions:
                return this._volumeLimitsHelper.getLimitValues(account, assetBalance, InstrumentTypes.OPTIONS)
                    .limitValue;
            case AccountFeature.PowerOfAttorney:
                return 0;
            case AccountFeature.BuyingPower:
                initialMarginSize = this.GetInitialMarginSize(account);
                if (initialMarginSize === 0 || initialMarginSize === null) {
                    return -1;
                }
                return assetBalance.AvailableFunds / initialMarginSize;
            case AccountFeature.GivenLeverage:
                initialMarginSize = this.GetInitialMarginSize(account);
                if (initialMarginSize === 0 || initialMarginSize === null) {
                    return -1;
                }
                //                100/Init %
                return Math.round(1 / initialMarginSize);
            case AccountFeature.OpenBalance:
                return account.Statistics.BeginProjectedBalance || NaN;
            case AccountFeature.StopOut:
                return assetBalance.StopOutValue;
            case AccountFeature.DayTraderPatternProtection:
                return account.DayTraderPatternProtection;
            case AccountFeature.AvailableDayTrades:
                return account.AvailableDayTrades;
            case AccountFeature.MaxDrawDownLevel:
                return account.MaxDrawdownLevel !== null && account.MaxDrawdownLevel !== -1
                    ? account.MaxDrawdownLevel
                    : 0;
            case AccountFeature.MaxRelativeDrawDownLevel:
                return account.MaxRelativeDrawDownLevel !== null && account.MaxRelativeDrawDownLevel !== -1
                    ? account.MaxRelativeDrawDownLevel
                    : 0;
            case AccountFeature.LossLimitPerTrade:
                return account.LossLimitPerTrade !== null
                    ? (account.MaxDailyLossLimit * account.LossLimitPerTrade) / 100
                    : 0;
            case AccountFeature.IncomingFunds:
                return assetBalance.UnsettledDeposit;
            case AccountFeature.OpenNetPL:
                return account.ProfitNet;
            case AccountFeature.WaivedMargin:
                return account.WaivedMargin;
            case AccountFeature.OptionPremiumReq:
                return assetBalance.OptionPremiumReq;
            case AccountFeature.OpenGrossPL:
                return account.Profit;
            case AccountFeature.OpenPostionsNumber:
                return account.AccountType === AccountType.MultiAsset
                    ? assetBalance.OpenPosNumber
                    : account.OpenPosNumber;
            case AccountFeature.OpenOrdersNumber:
                return account.AccountType === AccountType.MultiAsset
                    ? assetBalance.OpenOrdNumber
                    : account.OpenOrdNumber;
            case AccountFeature.TodaysNetProfit:
                return assetBalance.TodayRealizedPNL;
            case AccountFeature.TodaysFee:
                return assetBalance.TodayFees;
            case AccountFeature.TodayGrossPNL:
                return assetBalance.TodayGrossPNL;
            case AccountFeature.TodayVolume:
                return assetBalance.TodayVolume ? assetBalance.TodayVolume : 0.0;
            case AccountFeature.TodayTrades:
                return assetBalance.TodayTrades ? assetBalance.TodayTrades : 0.0;
            case AccountFeature.EODTrailingDrawdownLevel:
                return assetBalance.EODTrailingDrawdownLevel;
            case AccountFeature.RelativeDailyLossLimit:
                return assetBalance.CalculatedRelativeDailyLossLimit;
            case AccountFeature.CurrentDailyLoss:
                return assetBalance.CurrentDailyLoss;
            case AccountFeature.CurrentDailyProfit:
                return assetBalance.CurrentDailyProfit || 0;
            case AccountFeature.LiveLeverage: {
                const projBalance = Account.GetAccountFeature(AccountFeature.ProjectedBalance, account, assetBalance);
                return projBalance === 0 ? NaN : account.PositionsValue / projBalance;
            }
            case AccountFeature.TodaysPnLPercent: {
                const deposits = (_a = account.Statistics.AccountOperationStatistic.get(AccountOperationType.Deposit)) !== null && _a !== void 0 ? _a : 0;
                const withdrawals = (_b = account.Statistics.AccountOperationStatistic.get(AccountOperationType.Withdraw)) !== null && _b !== void 0 ? _b : 0;
                const transfers = (_c = account.Statistics.AccountOperationStatistic.get(AccountOperationType.Transfer)) !== null && _c !== void 0 ? _c : 0;
                const transferFees = (_d = account.Statistics.AccountOperationStatistic.get(AccountOperationType.TransferFee)) !== null && _d !== void 0 ? _d : 0;
                const withdrawalFees = (_e = account.Statistics.AccountOperationStatistic.get(AccountOperationType.withdrawalfee)) !== null && _e !== void 0 ? _e : 0;
                const balanceDebit = (_f = account.Statistics.AccountOperationStatistic.get(AccountOperationType.BalanceDebit)) !== null && _f !== void 0 ? _f : 0;
                const balanceCredit = (_g = account.Statistics.AccountOperationStatistic.get(AccountOperationType.BalanceCredit)) !== null && _g !== void 0 ? _g : 0;
                const adjustments = (_h = account.Statistics.AccountOperationStatistic.get(AccountOperationType.Adjustment)) !== null && _h !== void 0 ? _h : 0;
                const lastProjectedBalance = account.Statistics.BeginProjectedBalance;
                const projectedBalance = Account.GetAccountFeature(AccountFeature.ProjectedBalance, account, assetBalance, estimatedCurrency);
                const ID = projectedBalance -
                    deposits -
                    balanceCredit +
                    Math.abs(withdrawals) +
                    Math.abs(balanceDebit) -
                    transfers -
                    adjustments +
                    transferFees +
                    Math.abs(withdrawalFees);
                if (lastProjectedBalance === 0 || isNullOrUndefined(lastProjectedBalance)) {
                    const divider = projectedBalance - ID;
                    if (divider === 0) {
                        return 0;
                    }
                    else {
                        return (ID * 100) / divider;
                    }
                }
                else {
                    return ((ID - lastProjectedBalance) * 100) / lastProjectedBalance;
                }
            }
            case AccountFeature.AvailableCash:
                return assetBalance.AvailableCash;
            case AccountFeature.TotalPositionsValue:
                return assetBalance.TotalPositionsValue;
            case AccountFeature.FreeCommission:
                return account.FreeCommissionAmount;
            case AccountFeature.BlockedForFixedIncome:
                return assetBalance.BlockedForFixedIncome;
            case AccountFeature.FixedIncomeOrdersReq:
                return assetBalance.FixedIncomeOrdersReq;
            case AccountFeature.FixedIncomeValue:
                return account.fixedIncomeValue;
            default:
                return 0;
        }
    }
    static GetAccountFeatureString(value, key, account, assetBalance, estimatedCurrency = undefined, noCurrency = false) {
        if (estimatedCurrency === undefined) {
            estimatedCurrency = null;
        }
        switch (key) {
            case AccountFeature.Balance:
            case AccountFeature.ProjectedBalance:
            case AccountFeature.AvailableFunds:
            case AccountFeature.BlockedBalance:
            case AccountFeature.CashBalance:
            case AccountFeature.UnsettledCash:
            case AccountFeature.TodaysNetProfit:
            case AccountFeature.TodaysFee:
            case AccountFeature.TodayGrossPNL:
                return assetBalance.formatPriceExactly(value, noCurrency);
            case AccountFeature.WithdrawalAvailable:
                return assetBalance.formatPriceExactly(value === 0 ? 0 : value, noCurrency); // avoid -0   #95084
            case AccountFeature.MarginUsedPercent:
            case AccountFeature.RiskLevelPercent:
            case AccountFeature.WarnMarginReqPercent:
            case AccountFeature.MarginWarningLevel:
            case AccountFeature.TodaysPnLPercent:
            case AccountFeature.InterestRatePercent:
            case AccountFeature.NegativeInterestRatePercent:
                return PriceFormatter.formatPrice(MathUtils.TruncateDouble(value, 2), 2, false) + ' %';
            case AccountFeature.MarginUsed:
            case AccountFeature.MarginAvailable:
            case AccountFeature.WarningMargin:
            case AccountFeature.TotalMaintReq:
            case AccountFeature.BalancePlusAllRisks:
            case AccountFeature.CreditValue:
            case AccountFeature.BlockedForStocks:
            case AccountFeature.StocksOrdersReq:
            case AccountFeature.WarnMarginReq:
            case AccountFeature.MarginBeforeWarning:
            case AccountFeature.InitialMarginWithoutWaived:
            case AccountFeature.OpenBalance:
            case AccountFeature.StopOut:
            case AccountFeature.IncomingFunds:
            case AccountFeature.OptionPremiumReq:
            case AccountFeature.OpenGrossPL:
            case AccountFeature.EODTrailingDrawdownLevel:
            case AccountFeature.RelativeDailyLossLimit:
            case AccountFeature.CurrentDailyLoss:
            case AccountFeature.CurrentDailyProfit:
            case AccountFeature.AvailableCash:
            case AccountFeature.TotalPositionsValue:
            case AccountFeature.BlockedForFixedIncome:
            case AccountFeature.FixedIncomeOrdersReq:
            case AccountFeature.FixedIncomeValue:
                return assetBalance.formatPrice(value, noCurrency);
            case AccountFeature.BuyingPower:
                return assetBalance.formatPrice(value, noCurrency); // #108607 <- #108921
            case AccountFeature.GivenLeverage:
                return value && value >= 0
                    ? PriceFormatter.formatPrice(value, 0)
                    : Resources.getResource('general.N_A');
            case AccountFeature.EstimateValue:
                return estimatedCurrency != null
                    ? PriceFormatter.formatPrice(value, estimatedCurrency.Point) + ' ' + estimatedCurrency.Name
                    : assetBalance.formatPrice(value, noCurrency);
            case AccountFeature.StockValue:
                return assetBalance.formatPriceExactly(account.StockValue, noCurrency);
            case AccountFeature.OptionValue:
                return assetBalance.formatPriceExactly(account.OptionValue, noCurrency);
            case AccountFeature.StocksLiquidity:
                return assetBalance.formatPrice(assetBalance.StocksLiquidity, noCurrency);
            case AccountFeature.CurrentTradingStatus:
                return AccountTradeStatusHelper.GetLocalizedTradingStatus(account.AccountTradeStatus, account.StopTradingReason);
            case AccountFeature.VolumeLimitForAll:
                const allLimits = this._volumeLimitsHelper.getLimitValuesStr(account, assetBalance, InstrumentTypes.ALL);
                return this._volumeLimitsHelper.concatLimitValuesStr(allLimits.currentValueStr, allLimits.limitValueStr);
            case AccountFeature.VolumeLimitForEquities:
                const equitiesLimits = this._volumeLimitsHelper.getLimitValuesStr(account, assetBalance, InstrumentTypes.EQUITIES);
                return this._volumeLimitsHelper.concatLimitValuesStr(equitiesLimits.currentValueStr, equitiesLimits.limitValueStr);
            case AccountFeature.VolumeLimitForFutures:
                const futuresLimits = this._volumeLimitsHelper.getLimitValuesStr(account, assetBalance, InstrumentTypes.FUTURES);
                return this._volumeLimitsHelper.concatLimitValuesStr(futuresLimits.currentValueStr, futuresLimits.limitValueStr);
            case AccountFeature.VolumeLimitForOptions:
                const optionsLimits = this._volumeLimitsHelper.getLimitValuesStr(account, assetBalance, InstrumentTypes.OPTIONS);
                return this._volumeLimitsHelper.concatLimitValuesStr(optionsLimits.currentValueStr, optionsLimits.limitValueStr);
            case AccountFeature.PowerOfAttorney:
                // List<PowerOfAttorneyUser> powerOfAttorney = PowerOfAttorney.GetPoAUsersList(account);
                return ''; // powerOfAttorney.Count > 0  ? string.Join(", ", powerOfAttorney.Select(x => x.login)) : "";
            case AccountFeature.DayTraderPatternProtection:
                if (!account.DayTraderPattern) {
                    return '';
                }
                return Resources.getResource('panel.accounts.accountDetails.RiskManagement.Trading.State.' + (value ? 'Enabled' : 'Disabled'));
            case AccountFeature.AvailableDayTrades:
                if (!account.DayTraderPattern) {
                    return '';
                }
                return value !== null && value !== undefined ? value.toString() : '';
            case AccountFeature.MaxDrawDownLevel:
            case AccountFeature.MaxRelativeDrawDownLevel:
                return value !== -1 ? assetBalance.formatPrice(value, noCurrency) : '';
            case AccountFeature.LossLimitPerTrade:
                return value !== null && value !== undefined && !isNaN(value)
                    ? assetBalance.formatPrice(value, noCurrency)
                    : '';
            case AccountFeature.OpenNetPL:
                return value !== null && value !== undefined && !isNaN(value)
                    ? assetBalance.formatPrice(value, noCurrency)
                    : '';
            case AccountFeature.WaivedMargin:
                return value != null && value !== -1
                    ? assetBalance.formatPrice(value, noCurrency)
                    : StringUtils.NOT_AVAILABLE;
            case AccountFeature.OpenPostionsNumber:
            case AccountFeature.OpenOrdersNumber:
                return value === null || value === undefined ? '' : value.toString();
            case AccountFeature.TodayVolume:
                if (!isNaN(value)) {
                    return assetBalance.FDataCache.formatVolume(null, value, false, BaseSettings.productType, account);
                }
                else {
                    return Resources.getResource('general.Calculating');
                }
            case AccountFeature.LiveLeverage:
                return !isNaN(value)
                    ? PriceFormatter.formatPrice(MathUtils.TruncateDouble(value, 2), 2, false)
                    : StringUtils.NOT_AVAILABLE;
            case AccountFeature.TodayTrades:
                return !isNaN(value) ? value.toString() : Resources.getResource('general.Calculating');
            case AccountFeature.FreeCommission:
                return isValidNumber(value) ? assetBalance.formatPrice(value, noCurrency) : '';
            default:
                return '';
        }
    }
}
Account.FollowerIDsChanged = new CustomEvent();
Account.TradeStatusChanged = new CustomEvent();
Account.point = 2;
Account._volumeLimitsHelper = new VolumeLimitsHelper();
